import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { ModulePermission, PermissionResponse, ROUTE_MODULES, MODULE_NAV_CONFIG } from '../models/permission';
import { EncryptionService } from './encryption.service';

export interface NavItem {
  label: string;
  route: string;
  icon: string;
  children?: NavItem[];
  moduleName: string; // Module name for permission checking
}

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private http = inject(HttpClient);
  private encryptionService = inject(EncryptionService);
  private apiUrl = environment.apiBaseUrl;

  // Store all module permissions
  private permissionsSubject = new BehaviorSubject<ModulePermission[]>([]);
  public permissions$ = this.permissionsSubject.asObservable();

  // Store permissions loaded flag
  private permissionsLoadedSubject = new BehaviorSubject<boolean>(false);
  public permissionsLoaded$ = this.permissionsLoadedSubject.asObservable();

  constructor() {
    // Load permissions from encrypted localStorage on initialization
    const savedPermissions = this.loadPermissionsFromStorage();
    if (savedPermissions && savedPermissions.length > 0) {
      this.permissionsSubject.next(savedPermissions);
      this.permissionsLoadedSubject.next(true);
    }
  }

  /**
   * Fetch user permissions from API
   * Only for roleId 1, 2, 3
   */
  fetchUserPermissions(userId: string): Observable<PermissionResponse> {
    const url = `${this.apiUrl}staff/permissions/${userId}`;
    console.log('Fetching permissions from:', url);

    return this.http.get<PermissionResponse>(url).pipe(
      tap(response => {
        console.log('Permissions API response:', response);
        if (response.status && response.data) {
          this.setPermissions(response.data);
        }
      })
    );
  }

  /**
   * Check if role requires permission fetch
   */
  shouldFetchPermissions(roleId?: number | string): boolean {
    return roleId == 1 || roleId == 2 || roleId == 3;
  }

  /**
   * Refresh permissions for a user (used after OTP verify and SignalR updates)
   */
  refreshPermissionsForUser(userId?: string, roleId?: number | string): Observable<PermissionResponse | null> {
    if (!userId || !this.shouldFetchPermissions(roleId)) {
      return of(null);
    }
    return this.fetchUserPermissions(userId);
  }

  /**
   * Set permissions and save to localStorage
   */
  setPermissions(permissions: ModulePermission[]): void {
    this.permissionsSubject.next(permissions);
    this.permissionsLoadedSubject.next(true);
    this.savePermissionsToStorage(permissions);
  }

  /**
   * Get all permissions
   */
  getPermissions(): ModulePermission[] {
    return this.permissionsSubject.value;
  }

  /**
   * Get permission for a specific module by ID
   */
  getModulePermissionById(moduleId: number): ModulePermission | undefined {
    return this.permissionsSubject.value.find(p => p.moduleId === moduleId);
  }

  /**
   * Get permission for a specific module by name
   */
  getModulePermissionByName(moduleName: string): ModulePermission | undefined {
    return this.permissionsSubject.value.find(p => p.modules === moduleName);
  }

  /**
   * Get permission for a specific route
   */
  getModulePermissionByRoute(route: string): ModulePermission | undefined {
    // Remove query params and fragments
    const cleanRoute = route.split('?')[0].split('#')[0];
    
    // Check if route matches any module
    const moduleName = ROUTE_MODULES[cleanRoute];
    if (moduleName) {
      return this.getModulePermissionByName(moduleName);
    }

    // Check parent routes (e.g., /reports/claims -> /reports)
    const parts = cleanRoute.split('/').filter(p => p);
    for (let i = parts.length; i > 0; i--) {
      const parentRoute = '/' + parts.slice(0, i).join('/');
      const parentModuleName = ROUTE_MODULES[parentRoute];
      if (parentModuleName) {
        return this.getModulePermissionByName(parentModuleName);
      }
    }

    return undefined;
  }

  /**
   * Check if user has view permission for a route
   */
  canView(route: string): boolean {
    const permission = this.getModulePermissionByRoute(route);
    return permission ? permission.isView : false;
  }

  /**
   * Check if user has add permission for a module
   */
  canAdd(moduleName: string): boolean {
    const permission = this.getModulePermissionByName(moduleName);
    return permission ? permission.isAdd : false;
  }

  /**
   * Check if user has edit permission for a module
   */
  canEdit(moduleName: string): boolean {
    const permission = this.getModulePermissionByName(moduleName);
    return permission ? permission.isEdit : false;
  }

  /**
   * Check if user has delete permission for a module
   */
  canDelete(moduleName: string): boolean {
    const permission = this.getModulePermissionByName(moduleName);
    return permission ? permission.isDelete : false;
  }

  /**
   * Check if user has activate permission for a module
   */
  canActivate(moduleName: string): boolean {
    const permission = this.getModulePermissionByName(moduleName);
    return permission ? permission.isActivate : false;
  }

  /**
   * Check if user has all permissions for a module
   */
  hasAllPermissions(moduleName: string): boolean {
    const permission = this.getModulePermissionByName(moduleName);
    return permission ? permission.isAll : false;
  }

  /**
   * Check if a module exists in user's permissions
   */
  hasModuleInPermissions(moduleName: string): boolean {
    return this.permissionsSubject.value.some(p => p.modules === moduleName);
  }

  /**
   * Generate navigation items from user permissions
   * Only includes modules that user has view permission for
   */
  generateNavItems(): NavItem[] {
    const permissions = this.permissionsSubject.value;
    const navItems: NavItem[] = [];

    permissions.forEach(permission => {
      // Only add if module has view permission
      if (!permission.isView) {
        return;
      }

      const config = MODULE_NAV_CONFIG[permission.modules];
      if (!config) {
        // Module not configured in nav, skip it
        return;
      }

      const navItem: NavItem = {
        label: config.label || permission.modules,
        route: config.route,
        icon: config.icon,
        moduleName: permission.modules
      };

      // Add children if configured
      if (config.children) {
        navItem.children = config.children.map(child => ({
          label: child.label || '',
          route: child.route,
          icon: child.icon,
          moduleName: permission.modules // Children inherit parent module name
        }));
      }

      navItems.push(navItem);
    });

    return navItems;
  }

  /**
   * Clear all permissions (used on logout)
   */
  clearPermissions(): void {
    this.permissionsSubject.next([]);
    this.permissionsLoadedSubject.next(false);
    this.encryptionService.removeItem('userPermissions');
  }

  /**
   * Save permissions to encrypted localStorage
   */
  private savePermissionsToStorage(permissions: ModulePermission[]): void {
    this.encryptionService.setItem('userPermissions', permissions);
  }

  /**
   * Load permissions from encrypted localStorage
   */
  private loadPermissionsFromStorage(): ModulePermission[] | null {
    try {
      return this.encryptionService.getItem('userPermissions');
    } catch (e) {
      console.error('Error retrieving saved permissions:', e);
      return null;
    }
  }

  /**
   * Check if permissions are loaded
   */
  arePermissionsLoaded(): boolean {
    return this.permissionsLoadedSubject.value;
  }
}
